import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ModalLayout from "app/components/Layouts/ModalLayout/ModalLayout";
import styles from './styles.module.scss';
import { useTranslation } from "react-i18next";
import { FormProvider, useForm } from "react-hook-form";
import { isEmpty } from "lodash";
import FormInput from "app/components/Programs/FormInput";
import ButtonVariant from "app/components/Button/ButtonVariant";
import { schedulingFetchGet, schedulingFetchPatch } from "app/utils/request/scheduling/fetch";
import { toast } from "react-toastify";
import Label from "app/components/Programs/Label";
import PdfUploader from "app/components/Programs/ImageContainers/PdfUploader";
import useFlyerAttachment from "app/components/Programs/ImageContainers/PdfUploader/useFlyerAttachment";
import UserSelector from "app/components/Programs/UserSelector";
import { CoachProfileModel } from "app/models/scheduling/CoachProfileModel";
import { ProgramModel } from "app/models/scheduling/ProgramModel";
import UserAvatar from "app/components/Programs/UserAvatar";
import useProgramData from "app/routes/Sessions/hooks/useProgramData";
import { Loading } from "app/components/Wrappers";
import StateDayPicker from "app/components/Programs/StateDayPicker";
import { DateTime } from "luxon";
import SelectBox from "app/components/OptionButtonGroup/SelectBox";
import { generateHoursWithLabels, getHourOptionFromDateTime, hoursWithLablesFromSpecificValue } from "app/utils/timeUtils";
import useCalendarPreferences from "app/utils/hooks/useCalendarPreferences";

interface EditModalProps {
  isOpen: boolean;
  onClose: () => void;
  eventId: number;
  onSuccess?: () => void;
}

const constructDate = (date: string, hour: string, timeZone: string) => {
  let newDate = DateTime.fromISO(date).setZone(timeZone, {
    keepLocalTime: true,
  });

  const [hours, minutes] = hour.split(":");

  newDate = newDate.set({
    hour: parseInt(hours, 10),
    minute: parseInt(minutes, 10),
  });

  return newDate.toISO();
};

const EditProgramModal = ({
  isOpen,
  onClose,
  eventId,
  onSuccess
}: EditModalProps) => {
  const { t } = useTranslation();
  const { preferences } = useCalendarPreferences();
  const timeFormat = preferences.timeFormat;
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formErrors, setFormErrors] = useState({
    coaches: false,
    date: false,
    startTime: false,
    endTime: false,
    registrationOpenTime: false,
    registrationCloseTime: false,
    registrationOpenDate: false,
    registrationCloseDate: false
  });
  const [coaches, setCoaches] = useState<CoachProfileModel[]>([]);
  const [program, setProgram] = useState<ProgramModel>(null);
  const [date, setDate] = useState<Date | null>(null);
  const [startTime, setStartTime] = useState<{ value: string; label: string } | null>(null);
  const [endTime, setEndTime] = useState<{ value: string; label: string } | null>(null);
  const [registrationOpenDate, setRegistrationOpenDate] = useState<Date | null>(null);
  const [registrationCloseDate, setRegistrationCloseDate] = useState<Date | null>(null);
  const [registrationOpenTime, setRegistrationOpenTime] = useState<{ value: string; label: string } | null>(null);
  const [registrationCloseTime, setRegistrationCloseTime] = useState<{ value: string; label: string } | null>(null);
  const initialRegistration = useRef<{ start: string | null; end: string | null }>({ start: null, end: null });
  const flyer = useFlyerAttachment();
  const { reset: resetFlyer } = flyer;
  // CFTB-3442: the flyer of a recurring clinic is a property of the series, and
  // the athlete only ever sees the one on the program they open. Editing it per
  // occurrence let a coach change a flyer nobody reads, or wipe the series' own
  // one from the first occurrence. Only a standalone program owns its flyer here.
  const ownsFlyer = Boolean(program) && !(program as any)?.recurrenceGroup;

  const methods = useForm({
    defaultValues: {
      title: '',
      location: '',
      long_description: '',
    },
    mode: 'onChange',
  });

  const {
    handleSubmit,
    formState,
    reset,
  } = methods;

  useEffect(() => {
    if (!isOpen || !eventId) {
      return undefined;
    }

    let isCancelled = false;
    setLoading(true);

    const fetchProgramData = async () => {
      try {
        const program = await schedulingFetchGet(`/programs/${eventId}`);

        if (!isCancelled) {
          setProgram(program);
          reset({
            title: program.title,
            location: program.location,
            long_description: program.longDescription,
          });
          resetFlyer(program.flyerPdfUrl);
          setCoaches(program.coaches);
          setDate(DateTime.fromISO(program.start).toJSDate());
          setStartTime(getHourOptionFromDateTime(program.start, timeFormat));
          setEndTime(getHourOptionFromDateTime(program.end, timeFormat));
          setRegistrationOpenDate(DateTime.fromISO(program.registrationStartTime).toJSDate());
          setRegistrationOpenTime(getHourOptionFromDateTime(program.registrationStartTime, timeFormat));
          setRegistrationCloseDate(DateTime.fromISO(program.registrationEndTime).toJSDate());
          setRegistrationCloseTime(getHourOptionFromDateTime(program.registrationEndTime, timeFormat));
          initialRegistration.current = {
            start: program.registrationStartTime,
            end: program.registrationEndTime,
          };
          setLoading(false);
        }
      } catch (error) {
        if (!isCancelled) {
          console.error('Error fetching program data:', error);
          setLoading(false);
        }
      }
    };

    fetchProgramData();

    return () => {
      isCancelled = true;
    };
  }, [isOpen, eventId, reset, resetFlyer]);

  const validateForm = () => {
    const errors = {
      coaches: isEmpty(coaches),
      date: !date,
      startTime: !startTime,
      endTime: !endTime,
      registrationOpenDate: !registrationOpenDate,
      registrationOpenTime: !registrationOpenTime,
      registrationCloseDate: !registrationCloseDate,
      registrationCloseTime: !registrationCloseTime,
    };

    setFormErrors(errors);

    return !Object.values(errors).some(error => error);
  };

  const onSubmit = async (data) => {
    if (!validateForm()) {
      return;
    }

    setSubmitting(true);
    try {
      const coachIds = coaches.map((item) => item.id);

      const candidateRegistrationStart = constructDate(
        registrationOpenDate.toISOString(),
        registrationOpenTime?.value,
        program.timeZone,
      );
      const candidateRegistrationEnd = constructDate(
        registrationCloseDate.toISOString(),
        registrationCloseTime?.value,
        program.timeZone,
      );
      const sameInstant = (a, b) =>
        a && b && DateTime.fromISO(a).toMillis() === DateTime.fromISO(b).toMillis();
      const startUnchanged = sameInstant(candidateRegistrationStart, initialRegistration.current.start);
      const endUnchanged = sameInstant(candidateRegistrationEnd, initialRegistration.current.end);

      const startOffset = (program as any)?.registrationStartTimeOffset;
      const endOffset = (program as any)?.registrationEndTimeOffset;
      const rollingPayload = (offset) => ({ time_count: offset.timeCount, time_unit: offset.timeUnit });

      const payload: Record<string, unknown> = {
        title: data.title,
        location: data.location,
        long_description: data.long_description,
        coach_ids: coachIds,
        event: {
          start_time: constructDate(date.toISOString(), startTime?.value, program.timeZone),
          end_time: constructDate(date.toISOString(), endTime?.value, program.timeZone),
        },
      };
      if (!startUnchanged || !endUnchanged) {
        payload.registration_start_time = startUnchanged && startOffset
          ? rollingPayload(startOffset)
          : candidateRegistrationStart;
        payload.registration_end_time = endUnchanged && endOffset
          ? rollingPayload(endOffset)
          : candidateRegistrationEnd;
      }

      // CN-2693: left out when untouched, so saving the clinic can't clear a
      // flyer this modal never showed a change to. CFTB-3442: a clinic in a
      // series has no flyer of its own to send — the series form owns it.
      if (ownsFlyer) {
        const flyerPdfUrl = await flyer.resolveUrl();
        if (flyerPdfUrl !== undefined) {
          payload.flyer_pdf_url = flyerPdfUrl;
        }
      }

      await schedulingFetchPatch(`/programs/${eventId}/patch`, payload);
      toast.success(t("Program updated successfully"));
      onSuccess?.();
      onClose();
    } catch (error) {
      console.error("Error updating program:", error);
      toast.error(t("Failed to update program"));
    } finally {
      setSubmitting(false);
    }
  };

  const onRemoveCoach = (coach: CoachProfileModel) => {
    setCoaches((old) => old.filter((item) => item.id !== coach.id));
  };

  useEffect(() => {
    setFormErrors((prev) => ({ ...prev, coaches: isEmpty(coaches) }));
  }, [coaches]);

  const CoachItem = ({ coach, onClose }) => {
    const { firstName, lastName, email, profilePicture } = coach;
    let displayName = 'Coach';
    if (firstName && lastName) {
      displayName = `${firstName} ${lastName}`;
    } else if (email) {
      displayName = email;
    }

    const isPaymentCollector = coach.id === program?.paymentCollectorCoachProfileId;
    const hasRegistrationActivity =
      program?.registrationStatus === "Registration Open" ||
      program?.spotsBooked > 0;
    const canRemoveCoach = !(isPaymentCollector && hasRegistrationActivity);

    return (
      <div className={styles.coach_item_style} data-testid="coachItem">
        <div className={styles.coach_container}>
          <UserAvatar
            src={profilePicture}
            height={40}
            width={40}
            first_name={firstName}
            last_name={lastName}
          />
          <div className={styles.text_container}>
            <div className={styles.name}>{displayName}</div>
            {email && <div className={styles.email}>{email}</div>}
          </div>
        </div>
        {canRemoveCoach && (
          <div
            data-testid="remove"
            onClick={() => onClose(coach)}
            className={styles.close_style}
          >
            <i className="ico ico-x" />
          </div>
        )}
      </div>
    );
  };

  const CoachList = ({ userList }) => {
    return userList.map((item, index) => (
      <div key={item.id}>
        <CoachItem coach={item} onClose={onRemoveCoach} />
        {index + 1 < userList.length && (
          <div className={styles.line_separator} />
        )}
      </div>
    ));
  };

  const fetcher = useCallback((): Promise<[any[]]> => {
    if (!isOpen || !program?.facilityId) {
      return Promise.resolve([[]]);
    }

    const coachPromise = schedulingFetchGet(
      `/facilities/${program.facilityId}/coach_profiles`
    );

    return Promise.all([coachPromise]);
  }, [isOpen, program?.facilityId]);

  const [, coachData] = useProgramData(fetcher);

  const onDateChangeHandle = (value: string, label: string) => {
    const parsedDate = value ? DateTime.fromISO(value).toJSDate() : null;

    if (label === "registrationOpens") {
      setFormErrors((prev) => ({ ...prev, registrationOpenDate: parsedDate === null }));
      setRegistrationOpenDate(parsedDate);
    } else if (label === "registrationCloses") {
      setFormErrors((prev) => ({ ...prev, registrationCloseDate: parsedDate === null }));
      setRegistrationCloseDate(parsedDate);
    } else if (label === "date") {
      setFormErrors((prev) => ({ ...prev, date: parsedDate === null }));
      setDate(parsedDate);
    }
  };

  const calendar = useMemo(() => {
    return (
      <StateDayPicker
        onChange={(value: string) => onDateChangeHandle(value, 'date')}
        date={date}
        openToDate={date}
      />
    );
  }, [date]);

  const registrationOpenCalendar = useMemo(() => {
    return (
      <StateDayPicker
        onChange={(value: string) => onDateChangeHandle(value, 'registrationOpens')}
        date={registrationOpenDate}
        openToDate={registrationOpenDate}
      />
    );
  }, [registrationOpenDate]);

  const registrationCloseCalendar = useMemo(() => {
    return (
      <StateDayPicker
        onChange={(value: string) => onDateChangeHandle(value, "registrationCloses")}
        date={registrationCloseDate}
        openToDate={registrationCloseDate === null ? new Date() : null}
      />
    );
  }, [registrationCloseDate]);

  const onTimeChange = (time: { value: string; label: string } | null, label: string) => {
    if (label === "startTime") {
      setFormErrors((prev) => ({ ...prev, startTime: time === null }));
      setStartTime(time);
    } else if (label === "endTime") {
      setFormErrors((prev) => ({ ...prev, endTime: time === null }));
      setEndTime(time);
    } else if (label === "registrationOpenTime") {
      setFormErrors((prev) => ({ ...prev, registrationOpenTime: time === null }));
      setRegistrationOpenTime(time);
    } else if (label === "registrationCloseTime") {
      setFormErrors((prev) => ({ ...prev, registrationCloseTime: time === null }));
      setRegistrationCloseTime(time);
    }
  };

  if (loading) {
    return (
      <ModalLayout isOpen={isOpen} classNames={styles.modal} onClose={onClose}>
        <div className={styles.header}>
          <h2>{t('Edit Individual Clinic')}</h2>
          <button type="button" className={styles.close_button} onClick={onClose} aria-label="Close">
            <i className="ico ico-x" />
          </button>
        </div>
        <div className={styles.body}>
          <Loading isLoading={true} loadType='spinner' />
        </div>
      </ModalLayout>
    );
  }

  return (
    <ModalLayout isOpen={isOpen} classNames={styles.modal} onClose={onClose}>
      <div className={styles.header}>
        <h2>{t('Edit Individual Clinic')}</h2>
        <button type="button" className={styles.close_button} onClick={onClose} aria-label="Close">
          <i className="ico ico-x" />
        </button>
      </div>
      <FormProvider {...methods}>
        <form className={styles.body} onSubmit={handleSubmit(onSubmit)}>
          <div className={styles.side_container}>
            <FormInput
              tagTitle={t('Title')}
              fieldName='title'
              placeholder={t('Enter clinic name')}
              isRequired={true}
            />

            <FormInput
              tagTitle={t('Long Description')}
              fieldName='long_description'
              placeholder={t('Enter a long description')}
              isRequired={false}
              multiline={true}
            />

            <Label
              text={t('Date')}
              required={true}
              item={calendar}
              error={formErrors.date && t("This field is required")}
            />

            <div className={styles.row_container}>
              <Label
                text={t("Start Time")}
                item={(
                  <SelectBox
                    options={hoursWithLablesFromSpecificValue(
                      endTime,
                      "end",
                      15,
                      timeFormat,
                    )}
                    customColor="#e6e6e6"
                    value={startTime}
                    setValue={(time) => onTimeChange(time, "startTime")}
                  />
                )}
                showUnder
                containerStyle={styles.flex1}
                error={formErrors.startTime && t("This field is required")}
                required={true}
              />
              <Label
                text={t("End Time")}
                item={(
                  <SelectBox
                    options={hoursWithLablesFromSpecificValue(
                      startTime,
                      "start",
                      15,
                      timeFormat,
                    )}
                    customColor="#e6e6e6"
                    value={endTime}
                    setValue={(time: { value: string; label: string; }) => onTimeChange(time, "endTime")}
                  />
                )}
                showUnder
                containerStyle={styles.flex1}
                error={formErrors.endTime && t("This field is required")}
                required={true}
              />
            </div>

            <div className={styles.row_container}>
              <Label
                text={t("Registration Opens")}
                required={true}
                item={registrationOpenCalendar}
                error={formErrors.registrationOpenDate && t("This field is required")}
                containerStyle={styles.flex1}
              />
              <Label
                text={t("Time")}
                required={true}
                error={formErrors.registrationOpenTime && t("This field is required")}
                item={(
                  <SelectBox
                    options={generateHoursWithLabels(15, timeFormat)}
                    value={registrationOpenTime}
                    setValue={(time: { value: string; label: string; }) =>
                      onTimeChange(time, "registrationOpenTime")
                    }
                    customColor="#e6e6e6"
                    customControl={{
                      height: "40px",
                      borderRadius: "8px",
                    }}
                  />
                )}
                containerStyle={styles.flex1}
              />
            </div>

            <div className={`${styles.row_container} `}>
              <Label
                text={t("Registration Closes")}
                required={true}
                error={formErrors.registrationCloseDate && t("This field is required")}
                item={registrationCloseCalendar}
                containerStyle={styles.flex1}
              />
              <Label
                text={t("Time")}
                required={true}
                error={formErrors.registrationCloseTime && t("This field is required")}
                item={(
                  <SelectBox
                    options={generateHoursWithLabels(15, timeFormat)}
                    value={registrationCloseTime}
                    setValue={(time) =>
                      onTimeChange(time, "registrationCloseTime")
                    }
                    customColor="#e6e6e6"
                    customControl={{
                      height: "40px",
                      borderRadius: "8px",
                    }}
                  />
                )}
                containerStyle={styles.flex1}
              />
            </div>
          </div>
          <div className={styles.side_container}>
            <FormInput
              tagTitle={t('Location')}
              fieldName='location'
              isRequired={true}
            />

            {ownsFlyer && (
              <Label
                text={t('Clinic Flyer')}
                item={(
                  <PdfUploader
                    onFileSelect={flyer.onFileSelect}
                    onFileRemove={flyer.onFileRemove}
                    fileName={flyer.fileName}
                  />
                )}
              />
            )}

            <Label
              text={t('Coaches')}
              required={true}
              error={formErrors.coaches && t("This field is required")}
              item={(
                <UserSelector
                  placeholder={t("Select Coach")}
                  values={coachData.coaches[`${program?.facilityId}`]}
                  selectedUsers={coaches}
                  setValues={setCoaches}
                />
              )}
            />
            <CoachList userList={coaches} />
          </div>
        </form>
      </FormProvider>
      <div className={styles.bottom_container}>
        <ButtonVariant
          title={t("Cancel")}
          buttonType="secondary-filled"
          onClickFunc={onClose}
        />
        <ButtonVariant
          disabled={!formState.isValid || submitting}
          title={submitting ? t('Saving...') : t('Save')}
          buttonType="primary-filled"
          onClickFunc={handleSubmit(onSubmit)}
        />
      </div>
    </ModalLayout>
  );
};

export default EditProgramModal;
